import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    IoAdd,
    IoArrowBack,
    IoChatbubblesOutline,
    IoCheckmarkDoneOutline,
    IoCheckmarkOutline,
    IoCloseOutline,
    IoDocumentAttachOutline,
    IoNotificationsOutline,
    IoPaperPlaneOutline,
    IoPencilOutline,
    IoPeopleOutline,
    IoPersonAddOutline,
    IoPersonOutline,
    IoSearchOutline,
} from "react-icons/io5";

import GetData, { buildFileUrl } from "../../services/getData";
import { CHAT_POLL_INTERVAL, useChat } from "./ChatContext";
import "./chat.css";

const isOk = (data) => Boolean(data) && !(data instanceof Error);
const errText = (data, fallback) => {
    if (data instanceof Error) return data.message || fallback;
    return data?.message || fallback;
};

// Ограничения совпадают с серверными (chat/thread_messages.py).
const CHAT_FILE_MAX = 25 * 1024 * 1024;
const CHAT_FILE_EXT = [
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg",
    "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
    "txt", "csv", "zip", "rar", "7z",
];

const threadIcon = (thread) => {
    if (thread.type === "notifications") return <IoNotificationsOutline />;
    if (thread.type === "patient") return <IoPersonOutline />;
    if (thread.type === "staff") return <IoPeopleOutline />;
    return <IoChatbubblesOutline />;
};

const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const isImageFile = (name) => {
    const ext = String(name || "").split(".").pop().toLowerCase();
    return IMAGE_EXT.includes(ext);
};

/* -------------------------------------------------------------------------- */
/*  Вспомогательные компоненты                                                */
/* -------------------------------------------------------------------------- */

function PersonRow({ person, checked, onToggle, subtitle }) {
    return (
        <label className={`chat-person ${checked ? "is-checked" : ""}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(person.id)}
            />
            <span className="chat-person__avatar">
                {(person.name || "?").slice(0, 1).toUpperCase()}
            </span>
            <span className="chat-person__text">
                <span className="chat-person__name">{person.name || "—"}</span>
                {subtitle ? (
                    <span className="chat-person__sub">{subtitle}</span>
                ) : null}
            </span>
        </label>
    );
}

/* -------------------------------------------------------------------------- */
/*  Панель чата                                                               */
/* -------------------------------------------------------------------------- */

export default function ChatDrawer() {
    const { t, i18n } = useTranslation("chat");
    const {
        open,
        setOpen,
        threads,
        userId,
        refreshThreads,
        activeId,
        setActiveId,
        patchThread,
    } = useChat();

    const [tab, setTab] = useState("staff");
    const [mode, setMode] = useState("list"); // list | new | members | rename
    const [messages, setMessages] = useState([]);
    const [threadInfo, setThreadInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [people, setPeople] = useState({ staff: [], patients: [] });
    const [selected, setSelected] = useState([]);
    const [newType, setNewType] = useState("staff");
    const [newPatientId, setNewPatientId] = useState(null);
    const [titleDraft, setTitleDraft] = useState("");
    const [search, setSearch] = useState("");

    const lastIdRef = useRef(0);
    const messagesRef = useRef(null);
    const fileInputRef = useRef(null);
    const loadingRef = useRef(false);

    const locale = i18n.language || "ru";

    const activeThread = useMemo(
        () => threads.find((item) => Number(item.id) === Number(activeId)) || null,
        [threads, activeId],
    );

    const threadTitle = useCallback(
        (thread) => {
            if (!thread) return "";
            if (thread.type === "notifications") {
                return t("drawer.systemNotifications");
            }
            if (thread.type === "staff") {
                return t("drawer.systemStaff");
            }
            return thread.title || "—";
        },
        [t],
    );

    const staffThreads = useMemo(
        () => threads.filter((item) => item.type !== "patient"),
        [threads],
    );
    const patientThreads = useMemo(
        () => threads.filter((item) => item.type === "patient"),
        [threads],
    );

    /* ---------------------------------------------------------------------- */
    /*  Загрузка сообщений                                                     */
    /* ---------------------------------------------------------------------- */

    const markRead = useCallback(
        async (threadId, upToId) => {
            await GetData({
                module: "chat",
                block: "threads",
                action: "read",
                id_thread: threadId,
                up_to_id: upToId || 0,
            });
            refreshThreads();
        },
        [refreshThreads],
    );

    const loadMessages = useCallback(
        async (threadId, afterId = 0) => {
            if (!threadId || loadingRef.current) return;
            loadingRef.current = true;
            if (!afterId) {
                setLoading(true);
            }
            try {
                const data = await GetData({
                    module: "chat",
                    block: "thread_messages",
                    action: "list",
                    id_thread: threadId,
                    after_id: afterId,
                });
                if (!isOk(data) || !data.result) {
                    if (!afterId) {
                        setError(errText(data, t("drawer.error")));
                    }
                    return;
                }
                const incoming = Array.isArray(data.result.messages)
                    ? data.result.messages
                    : [];
                const meta = data.result.thread || null;
                if (meta) setThreadInfo(meta);

                let unreadIncoming = 0;
                if (afterId > 0) {
                    if (incoming.length > 0) {
                        setMessages((prev) => {
                            const known = new Set(prev.map((item) => item.id));
                            return [
                                ...prev,
                                ...incoming.filter((item) => !known.has(item.id)),
                            ];
                        });
                    }
                    unreadIncoming = incoming.filter(
                        (item) => Number(item.user_id) !== Number(userId),
                    ).length;
                } else {
                    setMessages(incoming);
                    unreadIncoming = incoming.filter(
                        (item) => Number(item.user_id) !== Number(userId),
                    ).length;
                }

                if (incoming.length > 0) {
                    lastIdRef.current = incoming[incoming.length - 1].id;
                } else if (!afterId) {
                    lastIdRef.current = Number(data.result.last_id) || 0;
                }

                // Помечаем прочитанным всё пришедшее (свои сообщения помечаются
                // на сервере при отправке).
                if (afterId === 0 || unreadIncoming > 0) {
                    const upTo = incoming.length
                        ? incoming[incoming.length - 1].id
                        : Number(data.result.last_id) || 0;
                    if (upTo > 0) {
                        await markRead(threadId, upTo);
                    }
                }
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        },
        [markRead, t, userId],
    );

    // Смена активного диалога — полная загрузка.
    useEffect(() => {
        if (!open || !activeId) {
            setMessages([]);
            setThreadInfo(null);
            lastIdRef.current = 0;
            return;
        }
        lastIdRef.current = 0;
        setError("");
        loadMessages(activeId, 0);
    }, [open, activeId, loadMessages]);

    // Опрос новых сообщений раз в 10 секунд.
    useEffect(() => {
        if (!open || !activeId) return undefined;
        const timer = setInterval(() => {
            loadMessages(activeId, lastIdRef.current);
        }, CHAT_POLL_INTERVAL);
        return () => clearInterval(timer);
    }, [open, activeId, loadMessages]);

    // Прокрутка к последнему сообщению (внутри ленты, страницу не двигаем).
    useEffect(() => {
        const container = messagesRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, activeId]);

    const loadPeople = useCallback(async () => {
        const data = await GetData({ module: "chat", block: "people" });
        if (isOk(data) && data.result) {
            setPeople({
                staff: Array.isArray(data.result.staff) ? data.result.staff : [],
                patients: Array.isArray(data.result.patients)
                    ? data.result.patients
                    : [],
            });
        }
    }, []);

    useEffect(() => {
        if (open && mode !== "list" && people.staff.length === 0) {
            loadPeople();
        }
    }, [open, mode, people.staff.length, loadPeople]);

    /* ---------------------------------------------------------------------- */
    /*  Действия                                                               */
    /* ---------------------------------------------------------------------- */

    const openThread = (thread) => {
        setActiveId(thread.id);
        setMode("list");
        setTab(thread.type === "patient" ? "patients" : "staff");
    };

    const handlePickFile = (event) => {
        const picked = event.target.files?.[0] || null;
        if (!picked) {
            setFile(null);
            return;
        }
        const ext = String(picked.name || "").split(".").pop().toLowerCase();
        if (!CHAT_FILE_EXT.includes(ext)) {
            setError(t("drawer.badFileType"));
            event.target.value = "";
            setFile(null);
            return;
        }
        if (picked.size > CHAT_FILE_MAX) {
            setError(t("drawer.fileTooBig"));
            event.target.value = "";
            setFile(null);
            return;
        }
        setError("");
        setFile(picked);
    };

    const clearFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSend = async () => {
        if (!activeId || sending) return;
        const value = text.trim();
        const attached = file;
        if (!value && !attached) {
            setError(t("drawer.emptyMessage"));
            return;
        }
        setSending(true);
        setError("");
        try {
            const data = await GetData(
                {
                    module: "chat",
                    block: "thread_messages",
                    action: "send",
                    id_thread: activeId,
                    text: value,
                },
                attached ? [attached] : undefined,
            );
            if (!isOk(data) || !data.result?.message) {
                setError(errText(data, t("drawer.sendFailed")));
                return;
            }
            const uploadErrors = Array.isArray(data.result.upload_errors)
                ? data.result.upload_errors
                : [];
            if (uploadErrors.length > 0) {
                setError(uploadErrors.join("; "));
            }
            const sent = data.result.message;
            // Файл был выбран, но сервер его не сохранил — сообщаем явно,
            // а сам файл оставляем, чтобы можно было повторить отправку.
            if (attached && !sent.file) {
                setError(
                    uploadErrors.length > 0
                        ? uploadErrors.join("; ")
                        : t("drawer.fileNotAttached"),
                );
            } else {
                setText("");
                clearFile();
            }
            setMessages((prev) =>
                prev.some((item) => item.id === sent.id) ? prev : [...prev, sent],
            );
            lastIdRef.current = Math.max(lastIdRef.current, sent.id);
            refreshThreads();
        } finally {
            setSending(false);
        }
    };

    const toggleSelected = (id) => {
        setSelected((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id],
        );
    };

    const startNewThread = (type) => {
        setMode("new");
        setNewType(type);
        setSelected([]);
        setNewPatientId(null);
        setTitleDraft("");
        setSearch("");
        setError("");
    };

    const createThread = async () => {
        setError("");
        if (newType === "patient" && !newPatientId) {
            setError(t("drawer.selectPatient"));
            return;
        }
        const memberIds = [...selected];
        const data = await GetData({
            module: "chat",
            block: "threads",
            action: "create",
            type: newType,
            title: titleDraft.trim(),
            member_ids: newType === "patient" ? memberIds : memberIds,
            id_patient: newType === "patient" ? newPatientId : 0,
        });
        if (!isOk(data) || !data.result?.id) {
            setError(errText(data, t("drawer.createFailed")));
            return;
        }
        await refreshThreads();
        setMode("list");
        setActiveId(Number(data.result.id));
    };

    const startRename = () => {
        setTitleDraft(activeThread ? threadTitle(activeThread) : "");
        setSelected([]);
        setSearch("");
        setError("");
        setMode("rename");
    };

    const saveRename = async () => {
        if (!activeId || !titleDraft.trim()) return;
        const data = await GetData({
            module: "chat",
            block: "threads",
            action: "rename",
            id_thread: activeId,
            title: titleDraft.trim(),
        });
        if (!isOk(data) || !data.result) {
            setError(errText(data, t("drawer.renameFailed")));
            return;
        }
        patchThread(activeId, { title: titleDraft.trim() });
        setThreadInfo((prev) =>
            prev ? { ...prev, title: titleDraft.trim() } : prev,
        );
        refreshThreads();
        setMode("list");
    };

    const startAddMembers = () => {
        setMode("members");
        setSelected([]);
        setSearch("");
        setError("");
    };

    const saveMembers = async () => {
        if (!activeId || selected.length === 0) {
            setMode("list");
            return;
        }
        const data = await GetData({
            module: "chat",
            block: "threads",
            action: "add_members",
            id_thread: activeId,
            member_ids: selected,
        });
        if (!isOk(data) || !data.result) {
            setError(errText(data, t("drawer.addFailed")));
            return;
        }
        await refreshThreads();
        setMode("list");
        loadMessages(activeId, 0);
    };

    /* ---------------------------------------------------------------------- */
    /*  Форматирование                                                         */
    /* ---------------------------------------------------------------------- */

    const formatTime = (value) => {
        const raw = String(value || "");
        if (!raw) return "";
        const date = new Date(raw.replace(" ", "T"));
        if (Number.isNaN(date.getTime())) return raw;
        const now = new Date();
        const sameDay =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();
        const time = date.toLocaleTimeString(locale, {
            hour: "2-digit",
            minute: "2-digit",
        });
        if (sameDay) return time;
        return `${date.toLocaleDateString(locale, {
            day: "2-digit",
            month: "2-digit",
        })} ${time}`;
    };

    const others = useMemo(
        () =>
            (threadInfo?.members || []).filter(
                (member) => Number(member.id) !== Number(userId),
            ),
        [threadInfo, userId],
    );

    const renderReceipt = (message) => {
        if (Number(message.user_id) !== Number(userId)) return null;
        if (others.length === 0) {
            return (
                <IoCheckmarkOutline
                    className="chat-msg__tick"
                    aria-label={t("drawer.sent")}
                />
            );
        }
        const readCount = others.filter(
            (member) => Number(member.last_read_id) >= Number(message.id),
        ).length;
        const readAll = readCount >= others.length;
        const label = readAll
            ? t("drawer.read")
            : t("drawer.readBy", { read: readCount, total: others.length });
        return (
            <IoCheckmarkDoneOutline
                className={`chat-msg__tick ${readAll ? "chat-msg__tick--read" : ""}`}
                aria-label={label}
                title={label}
            />
        );
    };

    /* ---------------------------------------------------------------------- */
    /*  Списки людей для выбора                                                */
    /* ---------------------------------------------------------------------- */

    const needle = search.trim().toLowerCase();
    const matches = (value) =>
        !needle || String(value || "").toLowerCase().includes(needle);

    const selectableStaff = people.staff.filter(
        (person) =>
            Number(person.id) !== Number(userId) && matches(person.name),
    );
    const selectablePatients = people.patients.filter((person) =>
        matches(person.name),
    );

    if (!open) return null;

    const modeTitle =
        mode === "new"
            ? t("drawer.newDialog")
            : mode === "members"
              ? t("drawer.addMembers")
              : mode === "rename"
                ? t("drawer.rename")
                : "";

    const showThread = Boolean(activeId);

    return (
        <div className="chat-root" role="dialog" aria-modal="true">
            <div className="chat-overlay" onClick={() => setOpen(false)} />

            <aside
                className={`chat-drawer ${
                    showThread || mode !== "list" ? "has-thread" : ""
                }`}>
                {/* ---------------- Левая колонка: список диалогов -------- */}
                <div className="chat-side">
                    <div className="chat-side__head">
                        <span className="chat-side__title">{t("drawer.title")}</span>
                        <button
                            type="button"
                            className="chat-icon-btn"
                            onClick={() => setOpen(false)}
                            title={t("drawer.close")}
                            aria-label={t("drawer.close")}>
                            <IoCloseOutline />
                        </button>
                    </div>

                    <div className="chat-tabs">
                        <button
                            type="button"
                            className={`chat-tabs__btn ${tab === "staff" ? "is-active" : ""}`}
                            onClick={() => setTab("staff")}>
                            <IoPeopleOutline />
                            {t("drawer.tabs.staff")}
                        </button>
                        <button
                            type="button"
                            className={`chat-tabs__btn ${tab === "patients" ? "is-active" : ""}`}
                            onClick={() => setTab("patients")}>
                            <IoPersonOutline />
                            {t("drawer.tabs.patients")}
                        </button>
                    </div>

                    <div className="chat-side__toolbar">
                        <button
                            type="button"
                            className="chat-new-btn"
                            onClick={() => startNewThread(tab === "patients" ? "patient" : "staff")}>
                            <IoAdd />
                            {t("drawer.newDialog")}
                        </button>
                    </div>

                    <ul className="chat-thread-list">
                        {(tab === "staff" ? staffThreads : patientThreads).map(
                            (thread) => (
                                <li key={thread.id}>
                                    <button
                                        type="button"
                                        className={`chat-thread ${
                                            Number(thread.id) === Number(activeId)
                                                ? "is-active"
                                                : ""
                                        }`}
                                        onClick={() => openThread(thread)}>
                                        <span className="chat-thread__icon">
                                            {threadIcon(thread)}
                                        </span>
                                        <span className="chat-thread__body">
                                            <span className="chat-thread__top">
                                                <span className="chat-thread__title">
                                                    {threadTitle(thread)}
                                                </span>
                                                <span className="chat-thread__date">
                                                    {formatTime(thread.updated_date)}
                                                </span>
                                            </span>
                                            <span className="chat-thread__bottom">
                                                <span className="chat-thread__last">
                                                    {thread.last_message?.has_file
                                                        ? t("drawer.fileAttached")
                                                        : thread.last_message?.text ||
                                                          t("drawer.noMessages")}
                                                </span>
                                                {thread.unread > 0 ? (
                                                    <span className="chat-thread__badge">
                                                        {thread.unread}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            ),
                        )}
                        {(tab === "staff" ? staffThreads : patientThreads).length ===
                        0 ? (
                            <li className="chat-empty">{t("drawer.emptyList")}</li>
                        ) : null}
                    </ul>
                </div>

                {/* ---------------- Правая колонка: диалог / формы -------- */}
                <div className="chat-main">
                    {mode === "list" && !showThread ? (
                        <div className="chat-placeholder">
                            <IoChatbubblesOutline size={34} />
                            <p>{t("drawer.selectDialog")}</p>
                        </div>
                    ) : null}

                    {mode === "list" && showThread ? (
                        <>
                            <div className="chat-main__head">
                                <button
                                    type="button"
                                    className="chat-icon-btn chat-back"
                                    onClick={() => setActiveId(null)}
                                    aria-label={t("drawer.back")}>
                                    <IoArrowBack />
                                </button>
                                <div className="chat-main__titles">
                                    <div className="chat-main__title">
                                        {activeThread
                                            ? threadTitle(activeThread)
                                            : threadInfo?.title || ""}
                                    </div>
                                    <div className="chat-main__sub">
                                        {others.length > 0
                                            ? t("drawer.members", {
                                                  count: (threadInfo?.members || [])
                                                      .length,
                                              })
                                            : ""}
                                    </div>
                                </div>
                                <div className="chat-main__actions">
                                    <button
                                        type="button"
                                        className="chat-icon-btn"
                                        onClick={startAddMembers}
                                        title={t("drawer.addMembers")}
                                        aria-label={t("drawer.addMembers")}>
                                        <IoPersonAddOutline />
                                    </button>
                                    <button
                                        type="button"
                                        className="chat-icon-btn"
                                        onClick={startRename}
                                        title={t("drawer.rename")}
                                        aria-label={t("drawer.rename")}>
                                        <IoPencilOutline />
                                    </button>
                                </div>
                            </div>

                            <div className="chat-messages" ref={messagesRef}>
                                {loading ? (
                                    <div className="chat-empty">
                                        {t("drawer.loading")}
                                    </div>
                                ) : null}
                                {!loading &&
                                messages.length === 0 &&
                                activeThread?.type === "notifications" ? (
                                    <div className="chat-empty">
                                        {t("drawer.notificationsEmpty")}
                                    </div>
                                ) : null}
                                {!loading &&
                                messages.length === 0 &&
                                activeThread?.type !== "notifications" ? (
                                    <div className="chat-empty">
                                        {t("drawer.emptyDialog")}
                                    </div>
                                ) : null}

                                {messages.map((message) => {
                                    const own =
                                        Number(message.user_id) === Number(userId);
                                    return (
                                        <div
                                            key={message.id}
                                            className={`chat-msg ${
                                                own ? "chat-msg--own" : ""
                                            }`}>
                                            {!own && (
                                                <span className="chat-msg__author">
                                                    {message.user_name ||
                                                        t("drawer.unknownUser")}
                                                </span>
                                            )}
                                            <div className="chat-msg__bubble">
                                                {message.text ? (
                                                    <span className="chat-msg__text">
                                                        {message.text}
                                                    </span>
                                                ) : null}
                                                {message.file ? (
                                                    isImageFile(
                                                        message.file.name,
                                                    ) ? (
                                                        <a
                                                            className="chat-msg__image-link"
                                                            href={buildFileUrl(
                                                                message.file.id,
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={
                                                                message.file
                                                                    .name ||
                                                                t("drawer.file")
                                                            }>
                                                            <img
                                                                className="chat-msg__image"
                                                                src={buildFileUrl(
                                                                    message.file
                                                                        .id,
                                                                )}
                                                                alt={
                                                                    message.file
                                                                        .name ||
                                                                    t(
                                                                        "drawer.file",
                                                                    )
                                                                }
                                                                loading="lazy"
                                                            />
                                                        </a>
                                                    ) : (
                                                        <a
                                                            className="chat-msg__file"
                                                            href={buildFileUrl(
                                                                message.file.id,
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer">
                                                            <IoDocumentAttachOutline />
                                                            <span>
                                                                {message.file
                                                                    .name ||
                                                                    t(
                                                                        "drawer.file",
                                                                    )}
                                                            </span>
                                                        </a>
                                                    )
                                                ) : null}
                                                <span className="chat-msg__meta">
                                                    <span className="chat-msg__time">
                                                        {formatTime(message.date)}
                                                    </span>
                                                    {renderReceipt(message)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {activeThread?.type === "notifications" ? (
                                <div className="chat-composer chat-composer--locked">
                                    {t("drawer.notificationsReadOnly")}
                                </div>
                            ) : (
                                <div className="chat-composer">
                                    <button
                                        type="button"
                                        className="chat-icon-btn chat-attach"
                                        onClick={() => fileInputRef.current?.click()}
                                        title={t("drawer.attach")}
                                        aria-label={t("drawer.attach")}>
                                        <IoDocumentAttachOutline />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="chat-attach__input"
                                        onChange={handlePickFile}
                                    />
                                    <textarea
                                        className="chat-composer__input"
                                        placeholder={t("drawer.inputPlaceholder")}
                                        value={text}
                                        rows={1}
                                        onChange={(event) =>
                                            setText(event.target.value)
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === "Enter" &&
                                                !event.shiftKey
                                            ) {
                                                event.preventDefault();
                                                handleSend();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="chat-send-btn"
                                        onClick={handleSend}
                                        disabled={sending}
                                        title={t("drawer.send")}
                                        aria-label={t("drawer.send")}>
                                        <IoPaperPlaneOutline />
                                    </button>
                                    {file ? (
                                        <button
                                            type="button"
                                            className="chat-attach__file"
                                            onClick={clearFile}
                                            title={t("drawer.removeFile")}>
                                            {file.name} ×
                                        </button>
                                    ) : null}
                                </div>
                            )}
                            {error ? (
                                <div className="chat-error">{error}</div>
                            ) : null}
                        </>
                    ) : null}

                    {mode !== "list" ? (
                        <div className="chat-form">
                            <div className="chat-main__head">
                                <button
                                    type="button"
                                    className="chat-icon-btn"
                                    onClick={() => setMode("list")}
                                    aria-label={t("drawer.cancel")}>
                                    <IoArrowBack />
                                </button>
                                <div className="chat-main__title">{modeTitle}</div>
                            </div>

                            {mode !== "rename" ? (
                                <>
                                    {mode === "new" ? (
                                        <div className="chat-form__types">
                                            <button
                                                type="button"
                                                className={`chat-tabs__btn ${
                                                    newType === "staff"
                                                        ? "is-active"
                                                        : ""
                                                }`}
                                                onClick={() => setNewType("staff")}>
                                                {t("drawer.tabs.staff")}
                                            </button>
                                            <button
                                                type="button"
                                                className={`chat-tabs__btn ${
                                                    newType === "patient"
                                                        ? "is-active"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    setNewType("patient")
                                                }>
                                                {t("drawer.tabs.patients")}
                                            </button>
                                        </div>
                                    ) : null}

                                    {mode === "new" &&
                                    newType === "patient" ? (
                                        <>
                                            <div className="chat-search">
                                                <IoSearchOutline />
                                                <input
                                                    type="text"
                                                    placeholder={t(
                                                        "drawer.searchPeople",
                                                    )}
                                                    value={search}
                                                    onChange={(event) =>
                                                        setSearch(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="chat-people">
                                                {selectablePatients.map(
                                                    (person) => (
                                                        <label
                                                            key={person.id}
                                                            className={`chat-person ${
                                                                Number(
                                                                    newPatientId,
                                                                ) ===
                                                                Number(person.id)
                                                                    ? "is-checked"
                                                                    : ""
                                                            }`}>
                                                            <input
                                                                type="radio"
                                                                name="chat-patient"
                                                                checked={
                                                                    Number(
                                                                        newPatientId,
                                                                    ) ===
                                                                    Number(
                                                                        person.id,
                                                                    )
                                                                }
                                                                onChange={() =>
                                                                    setNewPatientId(
                                                                        person.id,
                                                                    )
                                                                }
                                                            />
                                                            <span className="chat-person__avatar">
                                                                {(
                                                                    person.name ||
                                                                    "?"
                                                                )
                                                                    .slice(0, 1)
                                                                    .toUpperCase()}
                                                            </span>
                                                            <span className="chat-person__text">
                                                                <span className="chat-person__name">
                                                                    {person.name ||
                                                                        "—"}
                                                                </span>
                                                            </span>
                                                        </label>
                                                    ),
                                                )}
                                                {selectablePatients.length ===
                                                0 ? (
                                                    <div className="chat-empty">
                                                        {t("drawer.noResults")}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="chat-form__label">
                                                {t("drawer.staffLabel")}
                                            </div>
                                        </>
                                    ) : null}

                                    {mode === "members" ||
                                    newType === "staff" ||
                                    mode === "new" ? (
                                        <>
                                            {mode !== "new" ||
                                            newType === "staff" ? (
                                                <div className="chat-search">
                                                    <IoSearchOutline />
                                                    <input
                                                        type="text"
                                                        placeholder={t(
                                                            "drawer.searchPeople",
                                                        )}
                                                        value={search}
                                                        onChange={(event) =>
                                                            setSearch(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                            <div className="chat-people">
                                                {selectableStaff.map((person) => (
                                                    <PersonRow
                                                        key={person.id}
                                                        person={person}
                                                        checked={selected.includes(
                                                            person.id,
                                                        )}
                                                        onToggle={
                                                            toggleSelected
                                                        }
                                                        subtitle={person.post}
                                                    />
                                                ))}
                                                {selectableStaff.length === 0 ? (
                                                    <div className="chat-empty">
                                                        {t("drawer.noResults")}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </>
                                    ) : null}

                                    {mode === "new" ? (
                                        <input
                                            type="text"
                                            className="chat-input"
                                            placeholder={t(
                                                "drawer.titlePlaceholder",
                                            )}
                                            value={titleDraft}
                                            onChange={(event) =>
                                                setTitleDraft(event.target.value)
                                            }
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <input
                                    type="text"
                                    className="chat-input"
                                    placeholder={t("drawer.titlePlaceholder")}
                                    value={titleDraft}
                                    onChange={(event) =>
                                        setTitleDraft(event.target.value)
                                    }
                                />
                            )}

                            <div className="chat-form__actions">
                                <button
                                    type="button"
                                    className="chat-btn chat-btn--ghost"
                                    onClick={() => setMode("list")}>
                                    {t("drawer.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="chat-btn"
                                    onClick={
                                        mode === "rename"
                                            ? saveRename
                                            : mode === "members"
                                              ? saveMembers
                                              : createThread
                                    }>
                                    {mode === "members"
                                        ? t("drawer.addMembers")
                                        : t("drawer.save")}
                                </button>
                            </div>
                            {error ? (
                                <div className="chat-error">{error}</div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </aside>
        </div>
    );
}
